import { create } from 'zustand'
import { pageType, VodPage } from './types'
import { SearchResultType } from '@/types/search/searchResultType'
import {
    CachedItemDetail,
    EpisodeItemInterface,
    Offer,
    Season,
} from '@/types/vod/VodItemDetails'
import { VodRowViewProps } from '@/types/vod/vodRowType'
import { VODProps } from '@/types/vod/VODProps'
import { createJSONStorage, persist } from 'zustand/middleware'
import { persistStorage } from '@/shared/state/dataBase/persistStorage'
import { VodPageId, VodPageType } from '@/shared/constants'
import VodItem from '@/features/VOD/vodClasses/vodItem'
import VodDetails from '@/features/VOD/vodClasses/vodDetails'
import { ISeasonAndEpisodes } from '@/types/vod/seasonsAndEpisodes'

export interface IItemDetails {
    productId: string | null | undefined
    assetType: string | null | undefined
    id: string | null | undefined
    offers?: Offer[]
    serviceName?: string
}

export interface VODState {
    isVodContentPlaying: boolean
    isVodDetailsPageRendered: boolean
    actualPagePosition: number[] | []
    productId: string | null
    itemDetails:
        | {
              item: IItemDetails
              id: number
          }[]
        | null
    searchItemDetails: SearchResultType | null
    vodRows: {
        categoryId: string
        rows: VodRowViewProps[]
    }[]
    vodRowsCategories: {
        productId: string
        content: Array<VodRowViewProps>
    }[]
    vodRowItems: {
        rowId: string
        content: Array<VodItem>
    }[]
    vodPages: VodPage[]
    vodCachedDetails: CachedItemDetail[]
    vodRowItemType: string[]
    isVodContentVisible: boolean
    selectedVOD: VODProps | null
    isFetchContinuesWatching: boolean
    seasonsAndEpisodes: ISeasonAndEpisodes[]
    isLinkedFromHome: boolean
    playbackPosition: number
    actualSeason: Season | null
    actualEpisode: EpisodeItemInterface | null
    lastFocusedItem: string | null

    setLastFocusedItem: (id: string | null) => void
    setSelectedVOD: (vod: VODProps | null) => void
    setActualPagePosition: (value: number[] | []) => void
    setItemDetails: (item: IItemDetails | null) => void
    removeItemDetail: (index?: number) => void
    resetItemDetail: () => void
    setSearchItemDetails: (item: SearchResultType | null) => void
    setProductId: (item: string | null) => void
    setVodRows: (value: Array<VodRowViewProps>, id?: string) => void
    setVodRowsCategories: (
        productId: string,
        value: Array<VodRowViewProps>
    ) => void
    setVodRowItems: (rowId: string, value: Array<VodItem>) => void
    setVodPages: (
        itemId?: string,
        title?: string,
        type?: pageType,
        sliceIndex?: number
    ) => void
    setVodCachedDetails: (
        vodItemId: string,
        details?: VodDetails,
        remove?: boolean
    ) => void
    setVodRowItemType: (type?: string) => void
    setIsFetchContinuesWatching: (value: boolean) => void
    resetVodPages: () => void
    resetVODCache: () => void
    setIsVodContentPlaying: (value: boolean) => void
    setPlaybackPosition: (position: number) => void
    setClearVod: () => void
    setIsLinkedFromHome: (isLinked: boolean) => void
    setSeasonsAndEpisodes: (value: ISeasonAndEpisodes) => void
    getSeasonsAndEpisodes: (
        seasonId: string
    ) => EpisodeItemInterface[] | undefined
    setActualSeason: (season: Season | null) => void
    setActualEpisode: (episode: EpisodeItemInterface | null) => void
    setIsVodDetailsPageRendered: (isVodDetailsPageRendered: boolean) => void
}

export const useVODStore = create<VODState>()(
    persist(
        (set, get) => ({
            isVodContentPlaying: false,
            isVodDetailsPageRendered: false,
            isVodContentVisible: false,
            itemDetails: null,
            searchItemDetails: null,
            productId: null,
            actualPagePosition: [],
            vodRows: [],
            vodRowsCategories: [],
            vodRowItems: [],
            vodPages: [
                {
                    itemId: VodPageId,
                    title: 'VOD',
                    type: VodPageType,
                },
            ],
            vodCachedDetails: [],
            vodRowItemType: [],
            selectedVOD: null,
            isFetchContinuesWatching: false,
            isLinkedFromHome: false,
            playbackPosition: 0,
            seasonsAndEpisodes: [],
            lastFocusedItem: null,

            // seasons
            actualSeason: null,
            actualEpisode: null,

            setProductId: (value: string | null) =>
                set(() => ({ productId: value })),
            setActualPagePosition: (value: number[] | []) => {
                let newActualPagePosition: Set<number>
                if (value.length !== 0) {
                    newActualPagePosition = new Set([...value])
                }

                set(() => ({
                    actualPagePosition:
                        value.length !== 0
                            ? Array.from(newActualPagePosition)
                            : [],
                }))
            },
            setItemDetails: (item: IItemDetails | null) => {
                if (!item?.id || !item?.assetType || !item?.productId) return
                const { itemDetails, vodPages } = get()
                set(() => ({
                    itemDetails: itemDetails
                        ? [...itemDetails, { item, id: vodPages.length + 1 }]
                        : [{ item, id: vodPages.length + 1 }],
                }))
            },
            removeItemDetail: (index?: number) => {
                const { itemDetails } = get()
                const newItems = itemDetails

                if (index !== undefined && index >= 0) {
                    newItems?.splice(index)
                    set(() => ({
                        itemDetails: newItems,
                    }))
                    return
                } else if (index === undefined) {
                    set(() => ({
                        itemDetails: itemDetails?.slice(
                            0,
                            itemDetails.length - 1
                        ),
                    }))
                }
            },
            resetItemDetail: () => {
                set(() => ({
                    itemDetails: null,
                }))
            },
            setSearchItemDetails: (item: SearchResultType | null) =>
                set(() => ({
                    searchItemDetails: item,
                })),
            setSelectedVOD: (vod: VODProps | null) =>
                set(() => ({
                    selectedVOD: vod,
                })),
            setVodRows: (value: Array<VodRowViewProps>, id?: string) => {
                set((state) => {
                    const categoryId = id || 'withoutCategory'

                    const categoryIndex = state.vodRows.findIndex(
                        (cat) => cat.categoryId === categoryId
                    )

                    if (categoryIndex === -1) {
                        return {
                            vodRows: [
                                ...state.vodRows,
                                {
                                    categoryId,
                                    rows: value,
                                },
                            ],
                        }
                    } else {
                        const existingRows = state.vodRows[categoryIndex].rows
                        const newRowSet = new Set(
                            existingRows.map((row) => row.productId)
                        )

                        const uniqueRows = [
                            ...existingRows,
                            ...value.filter((row) => {
                                if (newRowSet.has(row.productId)) {
                                    return false
                                } else {
                                    newRowSet.add(row.productId)
                                    return true
                                }
                            }),
                        ]

                        return {
                            vodRows: state.vodRows.map((element, index) =>
                                index === categoryIndex
                                    ? { ...element, rows: uniqueRows }
                                    : element
                            ),
                        }
                    }
                })
            },
            setVodRowsCategories: (
                productId: string,
                value: Array<VodRowViewProps>
            ) => {
                const { vodRowsCategories } = get()

                const category = vodRowsCategories.find(
                    (item) => item.productId === productId
                )

                if (category === undefined) {
                    set(() => ({
                        vodRowsCategories: [
                            ...vodRowsCategories,
                            { productId, content: value },
                        ],
                    }))
                } else {
                    const updatedCategories = vodRowsCategories.map((item) =>
                        item.productId === productId
                            ? { ...item, content: value }
                            : item
                    )
                    set(() => ({
                        vodRowsCategories: updatedCategories,
                    }))
                }
            },
            setVodRowItems: (rowId: string, value: Array<VodItem>) => {
                set((state) => {
                    const { vodRowItems } = state
                    const rowIndex = vodRowItems.findIndex(
                        (item) => item.rowId === rowId
                    )

                    if (rowIndex === -1) {
                        return {
                            vodRowItems: [
                                ...vodRowItems,
                                { rowId, content: value },
                            ],
                        }
                    }

                    const updatedRowItems = vodRowItems.map((item, index) =>
                        index === rowIndex ? { ...item, content: value } : item
                    )

                    return { vodRowItems: updatedRowItems }
                })
            },
            setVodPages: (
                itemId?: string,
                title?: string,
                type?: pageType,
                sliceIndex?: number
            ) => {
                const { vodPages } = get()
                if (vodPages[vodPages.length - 1].itemId === itemId) return
                let indexes: VodPage[] = vodPages

                if (itemId && title && type) {
                    indexes.push({ itemId, title, type })
                } else if (indexes.length > 1) {
                    indexes = indexes.slice(0, sliceIndex || -1)
                }

                set(() => ({
                    vodPages: indexes,
                }))
            },
            resetVodPages: () =>
                set(() => ({
                    vodPages: [
                        {
                            itemId: VodPageId,
                            title: 'VOD',
                            type: VodPageType,
                        },
                    ],
                })),
            setVodCachedDetails: (
                vodItemId: string,
                details?: VodDetails,
                remove?: boolean
            ) => {
                const { vodCachedDetails } = get()
                let updatedDetailsList: CachedItemDetail[]
                if (remove) {
                    updatedDetailsList = vodCachedDetails.filter(
                        (item: CachedItemDetail) => item.vodItemId != vodItemId
                    )
                }

                if (details) {
                    const vodItemFromCache = vodCachedDetails.find(
                        (item) => item.vodItemId === vodItemId
                    )
                    if (vodItemFromCache) {
                        const filteredVodCachedDetails =
                            vodCachedDetails.filter(
                                (item) =>
                                    item.vodItemId !==
                                    vodItemFromCache.vodItemId
                            )
                        updatedDetailsList = [
                            ...filteredVodCachedDetails,
                            { vodItemId, details },
                        ]
                    } else {
                        updatedDetailsList = [
                            ...vodCachedDetails,
                            { vodItemId, details },
                        ]
                    }
                }

                set(() => ({
                    vodCachedDetails: updatedDetailsList,
                }))
            },
            setVodRowItemType: (type?: string) => {
                if (type) {
                    set((state) => ({
                        vodRowItemType: [...state.vodRowItemType, type],
                    }))
                } else {
                    set((state) => ({
                        vodRowItemType: state.vodRowItemType.slice(0, -1),
                    }))
                }
            },
            setIsFetchContinuesWatching: (value: boolean) =>
                set(() => ({
                    isFetchContinuesWatching: value,
                })),
            resetVODCache: () =>
                set(() => ({
                    isVodContentPlaying: false,
                })),
            setIsVodContentPlaying: (value: boolean) =>
                set(() => ({
                    isVodContentPlaying: value,
                })),

            setIsLinkedFromHome: (isLinked: boolean) =>
                set(() => ({
                    isLinkedFromHome: isLinked,
                })),
            setPlaybackPosition: (position: number) =>
                set(() => ({
                    playbackPosition: position,
                })),
            setClearVod: () =>
                set(() => ({
                    isVodContentPlaying: false,
                    actualPagePosition: [],
                    productId: null,
                    itemDetails: [],
                    searchItemDetails: null,
                    vodPages: [
                        {
                            itemId: VodPageId,
                            title: 'VOD',
                            type: VodPageType,
                        },
                    ],
                    vodRowItemType: [],
                    isVodContentVisible: false,
                    selectedVOD: null,
                    isFetchContinuesWatching: false,
                    isLinkedFromHome: false,
                    playbackPosition: 0,
                })),

            setSeasonsAndEpisodes: (value: ISeasonAndEpisodes) => {
                const { seasonsAndEpisodes } = get()
                const seasonIndex = seasonsAndEpisodes.findIndex(
                    (item) => item.seasonId === value.seasonId
                )

                if (seasonIndex !== -1) {
                    const existingEpisodes =
                        seasonsAndEpisodes[seasonIndex].episodes
                    const updatedEpisodes = Array.from(
                        new Set([...existingEpisodes, ...value.episodes])
                    )

                    const updatedSeasonsAndEpisodes = seasonsAndEpisodes.map(
                        (item, index) =>
                            index === seasonIndex
                                ? { ...item, episodes: updatedEpisodes }
                                : item
                    )

                    set(() => ({
                        seasonsAndEpisodes: updatedSeasonsAndEpisodes,
                    }))
                } else {
                    set(() => ({
                        seasonsAndEpisodes: [...seasonsAndEpisodes, value],
                    }))
                }
            },
            getSeasonsAndEpisodes: (seasonId: string) => {
                const { seasonsAndEpisodes } = get()
                const season = seasonsAndEpisodes.find(
                    (item) => item.seasonId === seasonId
                )
                return season ? season.episodes : undefined
            },
            setActualSeason: (season: Season | null) =>
                set(() => ({
                    actualSeason: season,
                })),
            setActualEpisode: (episode: EpisodeItemInterface | null) =>
                set(() => ({
                    actualEpisode: episode,
                })),
            setLastFocusedItem: (id: string | null) =>
                set(() => ({
                    lastFocusedItem: id,
                })),
            setIsVodDetailsPageRendered: (isVodDetailsPageRendered: boolean) =>
                set(() => ({
                    isVodDetailsPageRendered,
                })),
        }),
        {
            name: 'vod-storage',
            storage: createJSONStorage(() => persistStorage),
        }
    )
)
